import * as $ from 'jquery';
import { disposableSubscription, Lazy, serialDisposable } from 'common/barrels/core';
import { BehaviorSubject, Subject, Observable } from 'common/barrels/rx';
import { blockBusy } from 'common/dom/block';
import { camel } from 'common/functions/string';
import { maxDefaultZero } from 'common/functions/array';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import { registerBinding } from 'runtime/bindingengine';
import * as rxui from 'common/rxui/core';
import { LookupItem } from 'runtime/data/lookupitem';
import { LookupPropertyString } from 'runtime/data/lookupproperty';
import { LookupItemsRequested } from 'runtime/events/lookupitemsrequested';
import { ViewInfoRequested } from 'runtime/events/viewinforequested';
import * as rxtable from 'common/rxui/rxtable';

import 'runtime/styles/controls/tablelist.less';

var navKeys = { 40: 1, 38: -1 };

class ColumnInfo {
    constructor(public name: string, public displayName: string, public languageKey: string, public size: number, public sizeType: string, public isExtendedProperty: boolean) { }
}

export class TableList extends Control {
    private $inner: JQuery;
    private $table: JQuery;
    private $tbody: JQuery;
    private $thead: JQuery;

    private columnMetadata: ColumnInfo[];
    private value = new BehaviorSubject<string>(null);
    private items = new BehaviorSubject<LookupItem[]>(null);
    private sort = new BehaviorSubject<rxtable.ColumnSortRequest>(null);

    private autoClearance = 15;
    private minColumnWidth = 10;

    private resizerDisposable = serialDisposable(null);
    private loadingDisposable = serialDisposable(null);

    private updateRequired = new Subject<{}>();

    private viewInfo = new Lazy(() => this.eventAggregator.publish(new ViewInfoRequested()));

    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);

        this.$element.addClass('control-container rxui-table tablelist');
        var $outer = $('<div />').appendTo(this.$element);
        $('<div />').addClass('rxui-table-header-background').appendTo($outer);
        this.$inner = $('<div />').addClass('rxui-table-inner').appendTo($outer);
        this.$table = $('<table />').appendTo(this.$inner);
        this.$tbody = $('<tbody />').appendTo(this.$table);
        rxtable.createChock().appendTo(this.$table);

        this.columnMetadata = <ColumnInfo[]>(this.config['content.Configuration']);

        this.$thead = this.buildHeader(this.columnMetadata);

        this.applyTooltip(this.$element);

        var itemsSort = this.items.filter(x => x !== null).combineLatest(this.sort, (x, y) => ({ items: x, sort: y })).share();

        var key = rxui.keyEvent(this.$element).filter(() => this.derivedEnabledState() !== false).share();

        this.subscriptions = disposableSubscription(
            rxui.redrawRequired(this.updateRequired, this.container.activatedStream, this.visibilityStream, this.stretches() ? this.container.resizeStream() : Observable.never()).subscribe(() => this.resize()),

            rxui.click(this.$tbody)
                .filter(() => this.derivedEnabledState() !== false)
                .map(x => $(x.target).closest('tr')).filter(x => $.contains(this.$table[0], x[0])).subscribe(x => this.value.next(x.data('keyValue'))),

            rxui.click(this.$inner).map(x => ({ event: x, eventName: 'Clicked' }))
                .merge(rxui.dblclick(this.$inner).map(x => ({ event: x, eventName: 'DoubleClicked' })))
                .filter(x => !$.contains(this.$table.find('thead')[0], $(x.event.target)[0]))
                .filter(() => this.derivedEnabledState() !== false)
                .subscribe(x => this.triggerEvent(x.eventName)),

            key.map(x => ({ event: x, nav: navKeys[x.keyCode] })).filter(x => x.nav !== undefined).subscribe(x => {
                x.event.preventDefault();
                this.navigate(x.nav);
            }),

            key.filter(x => x.keyCode === $.ui.keyCode.ENTER).subscribe(() => this.triggerEvent('EnterPressed')),

            this.value.distinctUntilChanged().subscribe(x => this.$element.triggerHandler('content.SelectedValue', [x])),

            itemsSort.subscribe(x => this.populateTable(x.items, x.sort)),

            this.items.skip(1).subscribe(() => this.triggerEvent('Loaded')),
                
            itemsSort.filter(x => x.items !== null).combineLatest(this.value.distinctUntilChanged(),(x, y) => y).subscribe(x => {
                var $rows = this.$tbody.find('tr');
                $rows.removeClass('rxui-table-row-selected');

                var $row = new Lazy(() => $rows.toArray().map(x => $(x)).filter(y => y.data('keyValue') === x)[0] || null);
                if (x !== null && $row.value() !== null) {
                    $row.value().addClass('rxui-table-row-selected');
                    this.scrollItemIntoView($row.value(), $outer, this.$inner);
                }
            }),

            rxui.scrolled(this.$inner).switchMap(() => Observable.timer(0)).subscribe(() => rxtable.updateHeadingPositions(this.$table)),
            rxtable.makeHeadersDraggable(this.$table).subscribe(x => this.moveColumn(x.oldIndex, x.newIndex)),
            rxtable.makeSortable(this.$table).subscribe(x => this.sort.next(x)),

            this.processBindings(this.config),
            this.managePrimaryBinding('content.SelectedValue'),
            this.loadingDisposable
        );

        this.applyColumnResizingLogic();
        this.updateRequired.next({});

        this.initialize();
    }

    dispose() {
        super.dispose();
        this.resizerDisposable.dispose();
    }

    private applyColumnResizingLogic() {
        this.resizerDisposable.set(null);

        const subscription = rxtable.makeColumnsResizable(this.$table).subscribe(x => {
            var column = this.columnMetadata[x.index];
            column.sizeType = 'pixel';
            column.size = Math.max(x.size, this.minColumnWidth);
            this.updateRequired.next({});
        });

        this.resizerDisposable.set(disposableSubscription(subscription));
    }

    private moveColumn(oldIndex: number, newIndex: number) {
        // this prevents placing columns after the filler
        if (newIndex === this.columnMetadata.length)
            newIndex--;

        var move = oldIndex < newIndex
            ? (tr: JQuery) => tr.children().eq(oldIndex).insertAfter(tr.children().eq(newIndex))
            : (tr: JQuery) => tr.children().eq(oldIndex).insertBefore(tr.children().eq(newIndex));

        // reorder all existing cells (including headers)
        this.$table.find('tr').toArray().map(x => $(x)).forEach(x => move(x));
        rxtable.updateHeadingPositions(this.$table);

        // reorder metadata
        var newMetadata = this.columnMetadata;
        newMetadata.splice(newIndex, 0, newMetadata.splice(oldIndex, 1)[0]);
        this.columnMetadata = newMetadata;

        this.applyColumnResizingLogic();
    }

    private navigate(offset: number) {
        var $rows = this.$tbody.find('tr');
        var $current = $rows.filter('.rxui-table-row-selected');
        var currentIndex = $current.length === 0 ? -1 : $rows.index($current);
        var newIndex = Math.min(Math.max(0, currentIndex + offset), $rows.length - 1);
        this.value.next($rows.eq(newIndex).data('keyValue'));
    }

    load() {
        const blocker = blockBusy(this.$element);

        const subscription = this.eventAggregator
            .request(new LookupItemsRequested(this.fieldBindingSource('Content.SelectedValue'), this.fieldContext))
            .do(() => blocker.dispose())
            .subscribe(x => this.items.next(x));

        this.loadingDisposable.set(disposableSubscription(subscription));
    }

    clear() {
        this.value.next(null);
        this.items.next([]);
    }

    clearSelection() {
        this.value.next(null);
    }

    focus() {
        this.$inner.focus();
    }

    applyEnabledState(value: boolean) {
        this.$element.toggleClass('tablelist-disabled', !value);
    }

    selectFirstItem() {
        this.items.first().filter(x => x && x.length > 0).map(x => x[0]).subscribe(x => this.value.next(x.keyValue));
    }

    'content.SelectedValue'(value: string) {
        this.checkInitialValueReceived();
        this.value.next(value);
    }

    private buildHeader(columns: ColumnInfo[]) {
        return $('<thead />')
            .addClass('noselect')
            .append(columns.reduce((a, x) => a.append(this.makeHeaderCell(x)), $('<tr />'))
            .append(this.makeHeaderFillerCell())).appendTo(this.$table);
    }

    private makeHeaderCell(meta: ColumnInfo) {
        // if using local strings and using an auto-sized sizeType, an update must be triggered on arrival of the string
        const triggerResizeIfNecessary = meta.sizeType === 'sizeToHeader' || meta.sizeType === 'auto'
            ? () => window.setTimeout(() => this.updateRequired.next({}))
            : () => { };

        const text = meta.languageKey
            ? this.resources.getString(this.viewInfo.value().definitionName, meta.languageKey).do(_ => triggerResizeIfNecessary())
            : meta.displayName;

        var $th = $('<th />');
        var $outer = $('<div />').appendTo($th);
        $('<span />').bindText(text).appendTo($outer);

        return $th;
    }

    private makeHeaderFillerCell() {
        return $('<th />').append($('<div />').addClass('rxui-table-filler'));
    }

    private populateTable(items: LookupItem[], sort: rxtable.ColumnSortRequest) {
        var makeRow = (x: LookupItem) => this.columnMetadata.reduce((a, y) => a.append(this.makeCell(x, y)), $('<tr />')).data('keyValue', x.keyValue).append(this.makeFillerCell());

        var meta = new Lazy(() => this.columnMetadata[sort.index]);

        const value = (x: LookupItem) => this.getCellValue(x, meta.value());

        const ascending = (a: LookupItem, b: LookupItem) => value(a).localeCompare(value(b));
        const descending  = (b: LookupItem, a: LookupItem) => value(a).localeCompare(value(b));
        const sortedItems = !sort ? items : items.sort(sort.ascending ? ascending : descending);

        sortedItems.reduce((a, x) => a.append(makeRow(x)), this.$tbody.empty());

        if (sortedItems.length > 0)
            this.triggerEvent('FirstRowLoaded');

        this.$table.find('tr:odd').addClass('rxui-table-row-odd');
        this.$table.find('tr:even').addClass('rxui-table-row-even');
        this.updateRequired.next({});
    }

    private getCellValue(item: LookupItem, meta: ColumnInfo) {
        if (!meta.isExtendedProperty)
            return item[camel(meta.name)];

        var property = item.properties.filter(x => x instanceof LookupPropertyString).map(x => <LookupPropertyString>x).filter(x => x.name === meta.name)[0] || null;
        return property === null ? '' : property.value;
    }

    private makeCell(item: LookupItem, meta: ColumnInfo) {
        return $('<td />').append($('<div />').append($('<span />').text(this.getCellValue(item, meta))));
    }

    private makeFillerCell() {
        return $('<td />').addClass('rxui-table-filler');
    }

    private applyColumnWidths() {
        var numbered = this.columnMetadata.map((x, i) => ({ index: i, meta: x }));
        var nonStar = numbered
            .filter(x => x.meta.sizeType !== 'star')
            .map(x => ({ index: x.index, size: Math.max(this.getColumnSize(x.meta.sizeType, x.index, x.meta.size), this.minColumnWidth) }));

        var availableSpace = this.$table.width();

        var starSpace = availableSpace - nonStar.map(x => x.size).reduce((a, x) => a + x, 0);
        var rawStar = numbered.filter(x => x.meta.sizeType === 'star').map(x => ({ index: x.index, stars: x.meta.size }));
        var starTotal = rawStar.map(x => x.stars).reduce((a, x) => a + x, 0);
        var star = rawStar.map(x => ({ index: x.index, size: Math.max(starSpace * x.stars / starTotal, this.minColumnWidth) }));
            
        nonStar.forEach(x => this.$thead.find('tr>th').eq(x.index).width(x.size).css('min-width', x.size));
        star.forEach(x => this.$thead.find('tr>th').eq(x.index).width(x.size));

        this.$table.css('min-width', nonStar.map(x => x.size).reduce((a, x) => a + x, 0) + (star.length * this.minColumnWidth));

        this.manageFiller();
    }

    private getHeaderWidth(index: number) {
        return maxDefaultZero(this.$thead.find('tr>th').eq(index).children().children().toArray().map(x => $(x).width())) + this.autoClearance;
    }

    private getMaxCellWidth(index: number) {
        return maxDefaultZero(this.$tbody.find('tr').toArray()
            .map(x => $(x).find('td').eq(index))
            .map(x => x.children().children()).map(y => $(y).width())
            .reduce((a, x) => a.concat(x), [])
        ) + this.autoClearance;
    }

    private getColumnSize(sizeType: string, index: number, size: number) {
        return sizeType === 'sizeToHeader' ? this.getHeaderWidth(index)
                : sizeType === 'sizeToCells' ? this.getMaxCellWidth(index)
                : sizeType === 'auto' ? Math.max(this.getMaxCellWidth(index), this.getHeaderWidth(index))
                : size;
    }

    private manageFiller() {
        this.$table.find('.rxui-table-filler').toggleClass(
            'rxui-table-filler-noborder',
            maxDefaultZero(this.$tbody.find('.rxui-table-filler').toArray().map(x => $(x)).map(x => x.width())) <= 1 && !(this.$inner[0].scrollHeight > this.$inner.height())
        );
    }

    private resize() {
        var scrollLeftBefore = this.$inner.scrollLeft();
        var scrollTopBefore = this.$inner.scrollTop();
        this.applyColumnWidths();
        rxtable.updateHeadingPositions(this.$table);
        rxtable.resizeInternal(this.$table);
        this.$inner.scrollLeft(scrollLeftBefore);
        this.$inner.scrollTop(scrollTopBefore);
    }

    private scrollItemIntoView($item: JQuery, $container: JQuery, $inner) {
        // "why don't you use scrollIntoView()", I hear you say... IE.
        if ($item.position().top + $item.height() > $container.height())
            $inner[0].scrollTop = $item[0].offsetTop - $inner[0].clientHeight + $item.height();
                

        if ($item.position().top < $item.height())
            $inner[0].scrollTop = $item[0].offsetTop;
    }

    tabIndexElements() {
        return this.$inner;
    }
}

registerBinding('tablelist', TableList);